import axios from 'axios';

// Auto-detect backend URL based on current domain
const getBackendUrl = () => {
  // In production with custom domain, use the same origin
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    // If on custom domain (justroleit.fr), use it as backend
    if (hostname === 'justroleit.fr' || hostname === 'www.justroleit.fr') {
      return window.location.origin;
    }
    // If on emergent.host production domain
    if (hostname.endsWith('.emergent.host')) {
      return window.location.origin;
    }
  }
  // Fallback to env variable (for preview/development)
  return process.env.REACT_APP_BACKEND_URL;
};

export const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Helper function to get full URL for uploaded files (avatars, images, etc.)
export const getUploadUrl = (path) => {
  if (!path) return null;
  // Handle base64 data URLs (for avatars stored in DB)
  if (path.startsWith('data:')) return path;
  // Handle external URLs
  if (path.startsWith('http')) return path;
  // Handle legacy local paths (for backwards compatibility during transition)
  return `${BACKEND_URL}${path}`;
};

// Configure axios to send cookies
axios.defaults.withCredentials = true;

// ===== AUTH API =====

export const authAPI = {
  register: async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name });
    return response.data;
  },

  login: async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    return response.data;
  },

  googleCallback: async (sessionId) => {
    const response = await axios.post(`${API}/auth/google/callback`, { session_id: sessionId });
    return response.data;
  },

  getMe: async () => {
    const response = await axios.get(`${API}/auth/me`);
    return response.data;
  },

  logout: async () => {
    const response = await axios.post(`${API}/auth/logout`);
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await axios.put(`${API}/profile`, profileData);
    return response.data;
  },

  uploadAvatar: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axios.post(`${API}/profile/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await axios.post(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  },

  requestPasswordReset: async (email) => {
    const response = await axios.post(`${API}/auth/request-password-reset`, { email });
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await axios.post(`${API}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  }
};

// ===== EVENTS API =====

export const eventsAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/events`);
    return response.data;
  },

  create: async (eventData) => {
    const response = await axios.post(`${API}/events`, eventData);
    return response.data;
  },

  update: async (eventId, eventData) => {
    const response = await axios.put(`${API}/events/${eventId}`, eventData);
    return response.data;
  },

  delete: async (eventId) => {
    const response = await axios.delete(`${API}/events/${eventId}`);
    return response.data;
  },

  join: async (eventId) => {
    const response = await axios.post(`${API}/events/${eventId}/join`);
    return response.data;
  },

  leave: async (eventId) => {
    const response = await axios.post(`${API}/events/${eventId}/leave`);
    return response.data;
  }
};

// ===== GALLERY API =====

export const galleryAPI = {
  // Albums
  getAlbums: async () => {
    const response = await axios.get(`${API}/gallery/albums`);
    return response.data;
  },

  createAlbum: async (albumData) => {
    const response = await axios.post(`${API}/gallery/albums`, albumData);
    return response.data;
  },

  updateAlbum: async (albumId, albumData) => {
    const response = await axios.put(`${API}/gallery/albums/${albumId}`, albumData);
    return response.data;
  },

  deleteAlbum: async (albumId) => {
    const response = await axios.delete(`${API}/gallery/albums/${albumId}`);
    return response.data;
  },

  getAlbumImages: async (albumId) => {
    const response = await axios.get(`${API}/gallery/albums/${albumId}/images`);
    return response.data;
  },

  // Images
  getAll: async (albumId = null) => {
    const params = albumId ? `?album_id=${albumId}` : '';
    const response = await axios.get(`${API}/gallery${params}`);
    return response.data;
  },

  // Add image via URL
  addImage: async (imageUrl, title = "Sans titre", albumId = null) => {
    const response = await axios.post(`${API}/gallery`, {
      image_url: imageUrl,
      title: title,
      album_id: albumId
    });
    return response.data;
  },

  updateImage: async (imageId, data) => {
    const params = new URLSearchParams();
    if (data.album_id !== undefined) params.append('album_id', data.album_id || '');
    if (data.title) params.append('title', data.title);
    const response = await axios.put(`${API}/gallery/${imageId}?${params}`);
    return response.data;
  },

  delete: async (imageId) => {
    const response = await axios.delete(`${API}/gallery/${imageId}`);
    return response.data;
  }
};

// ===== MESSAGES API =====

export const messagesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/messages`);
    return response.data;
  },

  send: async (toUserId, content, messageType = 'private') => {
    const response = await axios.post(`${API}/messages`, {
      recipient_id: toUserId,
      content,
      is_broadcast: messageType === 'broadcast',
      message_type: messageType
    });
    return response.data;
  },

  getConversation: async (userId) => {
    const response = await axios.get(`${API}/messages/conversation/${userId}`);
    return response.data;
  },

  deleteMessage: async (messageId) => {
    const response = await axios.delete(`${API}/messages/${messageId}`);
    return response.data;
  },

  deleteConversation: async (userId) => {
    const response = await axios.delete(`${API}/messages/conversation/${userId}`);
    return response.data;
  },

  adminClearMessages: async (messageType) => {
    const response = await axios.delete(`${API}/messages/admin/clear/${messageType}`);
    return response.data;
  }
};

// ===== MEMBERS API =====

export const membersAPI = {
  getAll: async (role = null) => {
    const params = role ? `?role=${role}` : '';
    const response = await axios.get(`${API}/members${params}`);
    return response.data;
  },

  getBureau: async () => {
    const response = await axios.get(`${API}/members/bureau`);
    return response.data;
  },

  updateRole: async (userId, role) => {
    const response = await axios.put(`${API}/members/${userId}/role`, { role });
    return response.data;
  },

  updateBureauRole: async (userId, bureauRole) => {
    const response = await axios.put(`${API}/members/${userId}/bureau-role`, { bureau_role: bureauRole });
    return response.data;
  },

  updatePlayerStatus: async (userId, playerStatus) => {
    const response = await axios.put(`${API}/members/${userId}/player-status`, { player_status: playerStatus });
    return response.data;
  },

  updateTables: async (userId, tables) => {
    const response = await axios.put(`${API}/members/${userId}/tables`, { tables });
    return response.data;
  },

  delete: async (userId) => {
    const response = await axios.delete(`${API}/members/${userId}`);
    return response.data;
  }
};

// ===== NEWS API =====

export const newsAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/news`);
    return response.data;
  },

  create: async (newsData) => {
    const response = await axios.post(`${API}/news`, newsData);
    return response.data;
  },

  update: async (newsId, newsData) => {
    const response = await axios.put(`${API}/news/${newsId}`, newsData);
    return response.data;
  },

  delete: async (newsId) => {
    const response = await axios.delete(`${API}/news/${newsId}`);
    return response.data;
  },

  vote: async (newsId, option) => {
    const response = await axios.post(`${API}/news/${newsId}/vote?option=${encodeURIComponent(option)}`);
    return response.data;
  },

  // Comments
  getComments: async (newsId) => {
    const response = await axios.get(`${API}/news/${newsId}/comments`);
    return response.data;
  },

  addComment: async (newsId, content) => {
    const response = await axios.post(`${API}/news/${newsId}/comments`, { content });
    return response.data;
  },

  deleteComment: async (commentId) => {
    const response = await axios.delete(`${API}/comments/${commentId}`);
    return response.data;
  }
};

// ===== STATISTICS API =====

export const statsAPI = {
  get: async () => {
    const response = await axios.get(`${API}/stats`);
    return response.data;
  },
  
  getVisits: async (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.period) queryParams.append('period', params.period);
    if (params.date) queryParams.append('date', params.date);
    if (params.week_start) queryParams.append('week_start', params.week_start);
    if (params.month) queryParams.append('month', params.month);
    
    const response = await axios.get(`${API}/stats/visits?${queryParams.toString()}`);
    return response.data;
  },
  
  getActivity: async () => {
    const response = await axios.get(`${API}/stats/activity`);
    return response.data;
  }
};

// ===== ANALYTICS API =====

export const analyticsAPI = {
  pageView: async (page) => {
    try {
      await axios.post(`${API}/analytics/page`, { page });
    } catch (error) {
      // Silent fail - analytics should not break the app
      console.debug('Analytics skipped');
    }
  },
  
  articleView: async (articleId) => {
    try {
      await axios.post(`${API}/analytics/article/${articleId}`);
    } catch (error) {
      // Silent fail
      console.debug('Article analytics skipped');
    }
  }
};

// Keep old name for backward compatibility
export const trackingAPI = analyticsAPI;

// ===== GAMES LIBRARY API =====

export const gamesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/games`);
    return response.data;
  },
  
  create: async (gameData) => {
    const response = await axios.post(`${API}/games`, gameData);
    return response.data;
  },
  
  update: async (gameId, gameData) => {
    const response = await axios.put(`${API}/games/${gameId}`, gameData);
    return response.data;
  },
  
  delete: async (gameId) => {
    const response = await axios.delete(`${API}/games/${gameId}`);
    return response.data;
  }
};

// ===== NOTIFICATIONS API =====

export const notificationsAPI = {
  getAll: async (limit = 20) => {
    const response = await axios.get(`${API}/notifications?limit=${limit}`);
    return response.data;
  },
  
  getUnreadCount: async () => {
    const response = await axios.get(`${API}/notifications/unread-count`);
    return response.data;
  },
  
  markAsRead: async (notificationId) => {
    const response = await axios.post(`${API}/notifications/${notificationId}/read`);
    return response.data;
  },
  
  markAllAsRead: async () => {
    const response = await axios.post(`${API}/notifications/mark-all-read`);
    return response.data;
  }
};

// ===== ASSEMBLY NOTES API =====

export const assemblyNotesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/assembly-notes`);
    return response.data;
  },

  create: async (noteData) => {
    const response = await axios.post(`${API}/assembly-notes`, noteData);
    return response.data;
  },

  update: async (noteId, noteData) => {
    const response = await axios.put(`${API}/assembly-notes/${noteId}`, noteData);
    return response.data;
  },

  delete: async (noteId) => {
    const response = await axios.delete(`${API}/assembly-notes/${noteId}`);
    return response.data;
  }
};

// ===== FAQ API =====

export const faqAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/faq`);
    return response.data;
  },

  create: async (faqData) => {
    const response = await axios.post(`${API}/faq`, faqData);
    return response.data;
  },

  update: async (itemId, faqData) => {
    const response = await axios.put(`${API}/faq/${itemId}`, faqData);
    return response.data;
  },

  delete: async (itemId) => {
    const response = await axios.delete(`${API}/faq/${itemId}`);
    return response.data;
  },

  reorder: async (itemIds) => {
    const response = await axios.put(`${API}/faq/reorder/items`, { item_ids: itemIds });
    return response.data;
  }
};

// ===== DISCORD API =====

export const discordAPI = {
  getConfig: async () => {
    const response = await axios.get(`${API}/discord/config`);
    return response.data;
  },
  
  updateConfig: async (newsChannelId, eventsChannelId) => {
    const response = await axios.put(`${API}/discord/config`, { 
      news_channel_id: newsChannelId,
      events_channel_id: eventsChannelId
    });
    return response.data;
  },
  
  test: async (channelType = 'news') => {
    const response = await axios.post(`${API}/discord/test/${channelType}`);
    return response.data;
  },
  
  getGuilds: async () => {
    const response = await axios.get(`${API}/discord/guilds`);
    return response.data;
  },
  
  getGuildChannels: async (guildId) => {
    const response = await axios.get(`${API}/discord/guilds/${guildId}/channels`);
    return response.data;
  }
};

// ===== CONTACT API =====

export const contactAPI = {
  send: async (data) => {
    const response = await axios.post(`${API}/contact`, data);
    return response.data;
  },

  getAll: async () => {
    const response = await axios.get(`${API}/contact`);
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await axios.get(`${API}/contact/unread-count`);
    return response.data;
  },

  toggleRead: async (contactId) => {
    const response = await axios.put(`${API}/contact/${contactId}/read`);
    return response.data;
  },

  delete: async (contactId) => {
    const response = await axios.delete(`${API}/contact/${contactId}`);
    return response.data;
  }
};

// ===== SEARCH API =====

export const searchAPI = {
  search: async (query) => {
    const response = await axios.get(`${API}/search`, { params: { q: query } });
    return response.data;
  }
};

// ===== EXPORT API (CSV downloads) =====

const downloadCsv = async (url, fallbackName) => {
  const response = await axios.get(url, { responseType: 'blob' });
  const disposition = response.headers['content-disposition'] || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : fallbackName;
  const blobUrl = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(blobUrl);
  return filename;
};

export const exportAPI = {
  members: () => downloadCsv(`${API}/export/members.csv`, 'membres.csv'),
  events: () => downloadCsv(`${API}/export/events.csv`, 'evenements.csv'),
  stats: (start, end) => {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const qs = params.toString();
    return downloadCsv(`${API}/export/stats.csv${qs ? `?${qs}` : ''}`, 'statistiques.csv');
  }
};

// ===== LEGAL API =====

export const legalAPI = {
  get: async () => {
    const response = await axios.get(`${API}/legal`);
    return response.data;
  },

  update: async (data) => {
    const response = await axios.put(`${API}/legal`, data);
    return response.data;
  }
};

// ===== TABLES API =====

export const tablesAPI = {
  getAll: async () => {
    const response = await axios.get(`${API}/tables`);
    return response.data;
  },

  rename: async (tables) => {
    const response = await axios.put(`${API}/tables`, { tables });
    return response.data;
  }
};
